/**
 * Input/output contracts for the Survey bulk callables (ADR-054).
 *
 * Single-survey creation goes through direct Firestore writes from the
 * admin compose form (surveyService.createSurvey). The bulk path lives
 * here because every row potentially references a session by title
 * (resolves against this event's sessions), and the companion-notification
 * branch needs to write a sibling notification doc in the same
 * transaction as the survey.
 *
 * Scope: surveys are always anonymous in v1 (organizers read aggregates
 * only via the getSurveyResults callable). The Survey doc carries
 * `anonymous: true` unconditionally - no per-import override.
 *
 * `type` values match the Survey schema enum:
 *   - 'rating' - 1..5 scale, `options` must be empty
 *   - 'single_choice' - requires non-empty `options` (length >= 2)
 *   - 'free_text' - long-form response, `options` must be empty
 *
 * `sendNotification` toggles the companion push announcement. When true,
 * the bulk-create callable writes both docs in the same batch and links
 * survey.notificationId -> the new notification.id. Notifications carry
 * a deep link back to the survey (same path the compose form generates).
 *
 * Schedule semantics:
 *   - 'now' -> survey status='open', companion notification status='sent'.
 *   - 'scheduled' -> survey status='scheduled', companion notification
 *     status='scheduled'. processScheduledSurveys + processScheduled
 *     Notifications fire at the same `scheduledAt` moment.
 */

import { z } from 'zod';

export const bulkCreateSurveysRowSchema = z
  .object({
    question: z.string().min(1).max(1000),
    type: z.enum(['rating', 'single_choice', 'free_text']),
    /** Required when type='single_choice' (length >= 2); empty otherwise. */
    options: z.array(z.string().min(1).max(200)),
    /** Session title for session-scoped surveys. Null for event-wide surveys. */
    session: z.string().nullable(),
    scheduleMode: z.enum(['now', 'scheduled']),
    /** ISO-8601 datetime string (UTC). Required when scheduleMode='scheduled'. */
    scheduledAt: z.string().nullable(),
    sendNotification: z.boolean(),
    /** Required when sendNotification=true. */
    notificationTitle: z.string().nullable(),
    /** Required when sendNotification=true. */
    notificationBody: z.string().nullable(),
    /**
     * Optional Power BI presentation labels (per surveyDocumentSchema).
     * All null = "use the dashboard defaults," same fallback the admin
     * survey form applies when its label fields are left blank.
     */
    ratingAxisLabel: z.string().max(50).nullable(),
    ratingScaleMin: z.string().max(50).nullable(),
    ratingScaleMax: z.string().max(50).nullable(),
    optionAxisLabel: z.string().max(50).nullable(),
    responseLabel: z.string().max(50).nullable(),
    chartSubtitle: z.string().max(100).nullable(),
  })
  .strict();

export type BulkCreateSurveysRow = z.infer<typeof bulkCreateSurveysRowSchema>;

export const bulkCreateSurveysInputSchema = z
  .object({
    eventId: z.string().min(1),
    rows: z.array(bulkCreateSurveysRowSchema).min(1).max(200),
  })
  .strict();

export type BulkCreateSurveysInput = z.infer<
  typeof bulkCreateSurveysInputSchema
>;

export type BulkCreateSurveysRowStatus =
  | 'created'
  | 'unknown_session'
  | 'invalid_options'
  | 'invalid_schedule'
  | 'failed';

export interface BulkCreateSurveysRowResult {
  index: number;
  question: string;
  status: BulkCreateSurveysRowStatus;
  /** New survey document ID, populated when status === 'created'. */
  surveyId?: string;
  /** Companion notification ID when sendNotification=true and the row created. */
  notificationId?: string | null;
  /** Resolved session document ID when the row was scoped to a session. */
  sessionId?: string | null;
  message?: string;
}

export interface BulkCreateSurveysResponse {
  success: true;
  results: BulkCreateSurveysRowResult[];
  summary: {
    created: number;
    unknownSession: number;
    invalidOptions: number;
    invalidSchedule: number;
    failed: number;
  };
}

// ---------------------------------------------------------------------------
// bulkResolveSurveys - preview pass for the admin import dialog.
// Same input as bulkCreate. Does NOT write. Per row, reports whether
// the typed session title resolves to a known session in this event,
// and surfaces basic format diagnostics so the operator can fix the
// workbook before committing.
// ---------------------------------------------------------------------------

export const bulkResolveSurveysInputSchema = bulkCreateSurveysInputSchema;

export type BulkResolveSurveysInput = z.infer<
  typeof bulkResolveSurveysInputSchema
>;

export type BulkResolveSurveysRowStatus =
  | 'new'
  | 'unknown_session'
  | 'invalid_options'
  | 'invalid_schedule'
  | 'failed';

export interface BulkResolveSurveysProposal {
  /** Resolved session document ID. Populated when session matched. */
  sessionId?: string | null;
  /** Matched session title. Populated when session matched. */
  sessionTitle?: string | null;
  /** Canonical question text the row would land at (verbatim). */
  canonicalQuestion: string;
  /** Echo of sendNotification - lets the UI display a chip without re-reading. */
  willSendNotification: boolean;
}

export interface BulkResolveSurveysRowResult {
  index: number;
  typedQuestion: string;
  typedSession: string | null;
  status: BulkResolveSurveysRowStatus;
  proposal?: BulkResolveSurveysProposal;
  message?: string;
}

export interface BulkResolveSurveysResponse {
  success: true;
  results: BulkResolveSurveysRowResult[];
  summary: {
    new: number;
    unknownSession: number;
    invalidOptions: number;
    invalidSchedule: number;
    failed: number;
  };
}
